import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LinearProgress from '@mui/material/LinearProgress'
import DeleteIcon from '@mui/icons-material/Delete'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser'
import { useMarketingPartnersQuery, useDeleteMarketingPartnerMutation } from '../hooks/useDiscountCouponQuery'
import DeleteAmbassadorDialog from './DeleteAmbassadorDialog'

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

function AmbassadorsTab() {
  const navigate = useNavigate()
  const { allowedToGenerateAmbassadorCoupons } = useCurrentUser()
  const partnersQuery = useMarketingPartnersQuery()
  const deleteMutation = useDeleteMarketingPartnerMutation()

  const [partnerToDelete, setPartnerToDelete] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleConfirmDelete() {
    const partner = partnerToDelete
    deleteMutation.mutate(partner, {
      onSuccess: () => {
        setPartnerToDelete(null)
        notify(`Embaixador ${partner.name} excluído com sucesso`)
      },
      onError: (error) => notify(`Ocorreu um erro: ${error?.message || 'Erro desconhecido'}`),
    })
  }

  if (!allowedToGenerateAmbassadorCoupons) {
    return null
  }

  const partners = partnersQuery.data || []

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/workspace/discount-coupons/add/ambassador')}>
            Adicionar Embaixadores
          </Button>
        </Stack>
      </Paper>

      {partnersQuery.isPending && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Data de criação</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {partners.length === 0 && !partnersQuery.isPending && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum embaixador cadastrado.
                </TableCell>
              </TableRow>
            )}
            {partners.map((partner) => (
              <TableRow key={partner.id}>
                <TableCell>{partner.name}</TableCell>
                <TableCell>{partner.prefixCode}</TableCell>
                <TableCell>{formatDate(partner.createdAt)}</TableCell>
                <TableCell>
                  <IconButton
                    aria-label={`Excluir embaixador ${partner.name}`}
                    onClick={() => setPartnerToDelete(partner)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteAmbassadorDialog
        partner={partnerToDelete}
        open={Boolean(partnerToDelete)}
        onClose={() => setPartnerToDelete(null)}
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AmbassadorsTab
